/**
 * useBehaviorTracking - React hook for easy behavior tracking integration
 * 
 * Provides a simple API for components to track user behavior
 * without directly managing BehaviorTracker instances
 * 
 * Usage:
 *   const { trackClick, trackView, trackSearch } = useBehaviorTracking();
 *   
 *   trackClick(eventId, { position: 3 });
 *   trackView(eventId, duration);
 *   trackSearch('thai food', filters, results);
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { useLanguage } from '../contexts/LanguageContext';
import BehaviorTracker from '../services/tracking/BehaviorTracker';

export const useBehaviorTracking = (context = 'explore') => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const trackerRef = useRef(null);

  // Initialize tracker (only once per user session)
  useEffect(() => {
    // allow tracker for guests/anonymous too

    // Use global tracker if available, otherwise create new one
    if (window.globalBehaviorTracker) {
      trackerRef.current = window.globalBehaviorTracker;
    } else {
      trackerRef.current = new BehaviorTracker({
        userId: user?.userId || user?.id || null, // may be null -> anonymous
        context,
        language
      });
      window.globalBehaviorTracker = trackerRef.current;
    }

    return () => {
      // Don't cleanup global tracker
      if (trackerRef.current !== window.globalBehaviorTracker) {
        trackerRef.current?.cleanup();
      }
    };
  }, [user]);

  // Sync dynamic fields (language & context) without recreating tracker (3.1.2)
  useEffect(() => {
    if (!trackerRef.current) return;

    trackerRef.current.context = context;
    trackerRef.current.language = language;

    // Optional logging to verify sync
    if (process.env.NODE_ENV === 'development') {
      console.log('[useBehaviorTracking] Synced tracker context/lang', {
        context,
        language
      });
    }
  }, [context, language]);

  /**
   * Track a click interaction
   */
  const trackClick = useCallback((eventId, options = {}) => {
    if (!trackerRef.current || !eventId) return;

    trackerRef.current.trackInteraction({
      eventId,
      action: 'click',
      context,
      position: options.position || null,
      metadata: options.metadata || {},
      timestamp: Date.now()
    });
  }, [context]);

  /**
   * Track a like action
   */
  const trackLike = useCallback((eventId, options = {}) => {
    if (!trackerRef.current || !eventId) return;

    trackerRef.current.trackInteraction({
      eventId,
      action: 'like',
      context,
      metadata: options.metadata || {},
      timestamp: Date.now()
    });
  }, [context]);

  /**
   * Track a save/bookmark action
   */
  const trackSave = useCallback((eventId, options = {}) => {
    if (!trackerRef.current || !eventId) return;

    trackerRef.current.trackInteraction({
      eventId,
      action: 'save',
      context,
      metadata: options.metadata || {},
      timestamp: Date.now()
    });
  }, [context]);

  /**
   * Track a share action
   */
  const trackShare = useCallback((eventId, shareMethod = 'link', options = {}) => {
    if (!trackerRef.current || !eventId) return;

    trackerRef.current.trackInteraction({
      eventId,
      action: 'share',
      context,
      metadata: {
        shareMethod,
        ...options.metadata
      },
      timestamp: Date.now()
    });
  }, [context]);

  /**
   * Track a dislike action
   */
  const trackDislike = useCallback((eventId, options = {}) => {
    if (!trackerRef.current || !eventId) return;

    trackerRef.current.trackInteraction({
      eventId,
      action: 'dislike',
      context,
      metadata: options.metadata || {},
      timestamp: Date.now()
    });
  }, [context]);

  /**
   * Track event view duration
   */
  const trackView = useCallback((eventId, duration) => {
    if (!trackerRef.current || !eventId || !duration) return;

    trackerRef.current.trackEventView({
      eventId,
      duration,
      context,
      timestamp: Date.now()
    });
  }, [context]);

  /**
   * Track event impression (when shown to user)
   */
  const trackImpression = useCallback((eventIds) => {
    if (!trackerRef.current || !eventIds || eventIds.length === 0) return;

    trackerRef.current.trackEventImpression({
      eventIds: Array.isArray(eventIds) ? eventIds : [eventIds],
      context,
      timestamp: Date.now()
    });
  }, [context]);

  /**
   * Track search query
   */
  const trackSearch = useCallback((query, filters = {}, results = [], options = {}) => {
    if (!trackerRef.current) return;

    trackerRef.current.trackSearch({
      query,
      filters,
      resultsCount: results.length,
      context,
      timestamp: Date.now()
    });

    // NEW: Feed query into keyword interest (cleaned inside BehaviorTracker)
    if (query && String(query).trim().length >= 2) {
      const langGuess = filters.language || options.language || language;
      trackerRef.current.trackKeywordInterest(String(query), {
        source: 'search',
        weight: 0.2,  // Lower weight for search (0.15-0.30 range)
        lang: langGuess
      });
    }
  }, [context, language]);

  /**
   * Track search result selection
   */
  const trackSearchClick = useCallback((eventId, position, query, filters = {}) => {
    if (!trackerRef.current || !eventId) return;

    trackerRef.current.trackSearch({
      query,
      filters,
      selectedPosition: position,
      selectedEventId: eventId,
      context,
      timestamp: Date.now()
    });
  }, [context]);

  /**
   * Track location update
   */
  const trackLocation = useCallback((latitude, longitude, accuracy = null) => {
    if (!trackerRef.current || !latitude || !longitude) return;

    trackerRef.current.trackLocation({
      latitude,
      longitude,
      accuracy,
      context,
      timestamp: Date.now()
    });
  }, [context]);

  /**
   * Track location click on event
   */
  const trackLocationClick = useCallback((eventId, options = {}) => {
    if (!trackerRef.current || !eventId) return;

    trackerRef.current.trackInteraction({
      eventId,
      action: 'location_click',
      context,
      metadata: options.metadata || {},
      timestamp: Date.now()
    });
  }, [context]);

  /**
   * Manually flush tracking data to backend
   */
  const flush = useCallback(() => {
    if (!trackerRef.current) return;
    trackerRef.current.flush();
  }, []);

  /**
   * Mark an event as favorite (3.1.4)
   * A "favorite" is conceptually a save + an optional like
   */
  const markFavorite = useCallback((eventId, options = {}) => {
    if (!eventId) return;

    trackSave(eventId, options);

    if (options.alsoLike) {
      trackLike(eventId, options);
    }

    // In future we can also call a dedicated /favorites API here
  }, [trackSave, trackLike]);

  /**
   * Remove an event from favorites (3.1.4)
   */
  const unmarkFavorite = useCallback((eventId, options = {}) => {
    if (!trackerRef.current || !eventId) return;

    trackerRef.current.trackInteraction({
      eventId,
      action: options.action || 'unfavorite',  // can be 'unfavorite' or 'unsave'
      context,
      metadata: options.metadata || {},
      timestamp: Date.now()
    });
  }, [context]);

  return {
    // Interaction tracking
    trackClick,
    trackLike,
    trackSave,
    trackShare,
    trackDislike,
    trackLocationClick,

    // View tracking
    trackView,
    trackImpression,

    // Search tracking
    trackSearch,
    trackSearchClick,

    // Location tracking
    trackLocation,

    // Favorites abstraction (3.1.4)
    markFavorite,
    unmarkFavorite,

    // Utility
    flush,

    // Direct access to tracker (advanced usage)
    tracker: trackerRef.current
  };
};

export default useBehaviorTracking;